import React from "react";
import { Box, Button, GlobalStyles, Stack, Typography } from "@mui/material";
import {
  LoginOutlined,
  ExtensionOutlined,
  PhoneOutlined,
} from "@mui/icons-material";
import { Link as RouterLink } from "react-router-dom";
import Logo from "../../logo1_ai copy436_alpha.png";
import { tokens } from "../../theme/theme";
import { CHRISTMAS, FROST, HALLOWEEN, NIGHT, PIXEL_FONT, SPOOKY_FONT } from "../halloween/season";
import HalloweenHero from "../halloween/HalloweenHero";
import { WitchHat } from "../halloween/sprites";
import SantaFlight from "../christmas/SantaFlight";
import Twinkles from "../christmas/Twinkles";
import Sprite from "../christmas/Sprite";
import { ICONS } from "../christmas/atlas";

/**
 * Shown instead of the home page and the catalog while `maintenance` is true in
 * the `site_config` table — see MAINTENANCE.md.
 *
 * Deliberately says nothing about why: a visitor sees a finished-looking page
 * whose one prominent action is the realtor login, with quiet links to the two
 * public pages that stay open. Shared listing links, the crossword and the
 * contacts page keep working; only the two pages that list apartments are
 * replaced.
 *
 * The page never touches the listings database — it has to render precisely
 * when that data is what is being worked on.
 */

/* -------------------------------------------------------------------------- */
/* Parallax                                                                    */
/* -------------------------------------------------------------------------- */

/**
 * To change the artwork, just overwrite the two PNGs in `public/` — no code
 * change and no rebuild of this file needed. What matters for each image:
 *
 *   - transparent above the skyline, so the gradient below shows through;
 *   - the left and right edges should match, because the image is tiled
 *     horizontally and the join must be invisible;
 *   - keep both images the same aspect ratio, or set IMAGE_RATIO below.
 *
 * Current artwork is 2048x682 (exactly 3:1).
 */
const IMAGE_RATIO = 2048 / 682;

/** The bottom of the page gradient. Also what the browser canvas is painted. */
const PAGE_DARK = HALLOWEEN ? NIGHT.deep : CHRISTMAS ? FROST.deep : "#04142B";

/** `public/` is served from the site root; PUBLIC_URL covers a sub-path deploy. */
const asset = (name) => (process.env.PUBLIC_URL || "") + "/" + name;

/**
 * Back to front. `height` is how tall the layer is drawn on screen; the tile
 * width is derived from it so the artwork is never stretched. Nearer layers are
 * drawn larger and drift faster — that difference is the whole parallax effect.
 */
const DEFAULT_LAYERS = [
  {
    key: "back",
    file: "parallax_background.png",
    height: 800,
    duration: "150s",
    opacity: 0.9,
    /**
     * The background artwork is transparent above its sky and opaque below,
     * and that boundary is a hard horizontal line — on screen it reads as a
     * seam straight across the page. This fades the layer in through that
     * band so it blends into the gradient instead. Two percentages: fully
     * transparent above the first, fully opaque below the second. Set to null
     * if your own image has no such edge.
     */
    fade: "40% 60%",
  },
  {
    key: "front",
    file: "parallax_foreground.png",
    height: 520,
    duration: "68s",
    opacity: 1,
    // Nothing to fade: the rooftops are the top edge, which is the point.
    fade: null,
  },
];

/**
 * The Christmas art (built from "Christmas Art Assets/" by
 * scripts/christmas-assets). Each layer carries its own ratio:
 *
 *   - back: 2172x724, opaque, sky included — no fade; the page behind it is
 *     painted FROST.sky, the art's own top row, so taller screens just see
 *     more of the same sky above it;
 *   - front: the house row mirrored onto itself so it tiles without a seam,
 *     encoded at 3255x544.
 */
const CHRISTMAS_LAYERS = [
  {
    key: "back",
    file: "christmas/parallax_background_christmas.webp",
    ratio: 2172 / 724,
    height: 800,
    duration: "220s",
    opacity: 1,
    fade: null,
  },
  {
    key: "front",
    file: "christmas/parallax_foreground_christmas.webp",
    ratio: 3255 / 544,
    height: 520,
    duration: "100s",
    opacity: 1,
    fade: null,
  },
];

const LAYERS = CHRISTMAS ? CHRISTMAS_LAYERS : DEFAULT_LAYERS;

/** The pages that stay open during maintenance, minus the ones that list flats. */
const SECONDARY = [
  { label: "Кросворд дня", to: "/krosvord", icon: <ExtensionOutlined /> },
  { label: "Контакти", to: "/kontakty", icon: <PhoneOutlined /> },
];

export default function MaintenancePage() {
  return (
    <Box
      sx={{
        position: "relative",
        minHeight: "100vh",
        overflow: "hidden",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: HALLOWEEN
          ? "linear-gradient(175deg, " + NIGHT.dusk + " 0%, " + NIGHT.sky + " 48%, " + PAGE_DARK + " 100%)"
          : CHRISTMAS
          ? FROST.sky
          : "linear-gradient(175deg, " +
            tokens.blue800 +
            " 0%, " +
            tokens.blue900 +
            " 48%, " +
            PAGE_DARK +
            " 100%)",
      }}
    >
      {/*
        Dragging past either end of the page on iOS reveals the browser canvas
        behind the document, which is white — a bright flash above and below an
        otherwise dark page. The page's own gradient cannot cover it: that area
        is outside the document box.

        Two lines, because one is not enough on its own. `overscroll-behavior`
        removes the bounce entirely (Safari 16+, Chrome, Firefox) and is the
        real fix; the background colour is what older iOS falls back to. It is
        the gradient's own bottom colour, so the lower gutter is invisible —
        and above the page a darker band just reads as more night sky, whereas
        anything lighter than the page bottom reads as a seam.

        Scoped to this page via GlobalStyles — it is applied on mount and
        removed on unmount, so the light public pages are untouched.
      */}
      <GlobalStyles
        styles={{
          "html, body": {
            overscrollBehavior: "none",
            backgroundColor: PAGE_DARK,
          },
        }}
      />

      {/* Soft light behind the logo, so the mark does not sit on flat colour. */}
      <Box
        aria-hidden="true"
        sx={{
          position: "absolute",
          inset: 0,
          background: HALLOWEEN
            ? "radial-gradient(52% 36% at 50% 30%, rgba(255,140,50,0.2) 0%, rgba(255,140,50,0) 70%)"
            : CHRISTMAS
            ? "radial-gradient(52% 36% at 50% 30%, rgba(255,207,90,0.16) 0%, rgba(255,207,90,0) 70%)"
            : "radial-gradient(52% 36% at 50% 30%, rgba(46,139,245,0.34) 0%, rgba(46,139,245,0) 70%)",
        }}
      />

      {LAYERS.map(function (layer) {
        // Tile width comes from the layer height and the artwork's own ratio,
        // and the animation travels exactly one tile — that is what makes the
        // loop seamless. Deriving it (rather than hard-coding) means swapping in
        // a different image size cannot desynchronise the loop.
        const tile = Math.round(layer.height * (layer.ratio || IMAGE_RATIO));
        const stops = layer.fade ? layer.fade.split(" ") : null;
        const mask = stops
          ? "linear-gradient(to bottom, rgba(0,0,0,0) " +
            stops[0] +
            ", rgba(0,0,0,1) " +
            stops[1] +
            ")"
          : "none";
        return (
          <Box
            key={layer.key}
            aria-hidden="true"
            sx={{
              position: "absolute",
              left: 0,
              right: 0,
              bottom: 0,
              height: layer.height,
              opacity: layer.opacity,
              // Halloween: the same artwork, dimmed to silhouettes whose windows
              // still glow warm against the purple sky.
              filter: HALLOWEEN ? "brightness(0.6) saturate(0.8)" : "none",
              backgroundImage: 'url("' + asset(layer.file) + '")',
              backgroundRepeat: "repeat-x",
              backgroundPosition: "bottom left",
              backgroundSize: tile + "px " + layer.height + "px",
              // Safari still wants the prefix for masks.
              maskImage: mask,
              WebkitMaskImage: mask,
              // Computed key: each layer needs its own keyframes, because each
              // travels a different distance.
              ["@keyframes leocityParallaxDrift" + layer.key]: {
                from: { backgroundPositionX: "0px" },
                to: { backgroundPositionX: "-" + tile + "px" },
              },
              animation:
                "leocityParallaxDrift" +
                layer.key +
                " " +
                layer.duration +
                " linear infinite",
              "@media (prefers-reduced-motion: reduce)": { animation: "none" },
            }}
          />
        );
      })}

      {HALLOWEEN && <HalloweenHero roofs={false} />}
      {CHRISTMAS && <Twinkles count={12} seed={7} area={[3, 42]} />}
      {CHRISTMAS && <SantaFlight top={28} band={210} every={20} />}
      {CHRISTMAS && (
        // A snowman and a present out on the street of the foreground art.
        <Box
          aria-hidden="true"
          sx={{ position: "absolute", left: 0, right: 0, bottom: { xs: 4, md: 8 }, pointerEvents: "none" }}
        >
          <Sprite atlas={ICONS} name="snowman" scale={0.8} style={{ position: "absolute", bottom: 0, left: "5%" }} />
          <Sprite atlas={ICONS} name="gift" scale={0.5} style={{ position: "absolute", bottom: 0, right: "8%" }} />
        </Box>
      )}

      <Stack
        alignItems="center"
        spacing={2.5}
        sx={{
          position: "relative",
          px: 3,
          pb: { xs: 30, md: 26 },
          textAlign: "center",
        }}
      >
        <Box sx={{ position: "relative", display: "flex" }}>
          <Box
            component="img"
            src={Logo}
            alt="LeoCity"
            sx={{
              width: { xs: 128, md: 156 },
              // The logo file is solid black on transparency. brightness(0) flattens
              // it to black whatever the source pixels are, invert(1) turns that
              // white — alpha is preserved, so no second asset is needed.
              filter: "brightness(0) invert(1)",
              opacity: 0.96,
            }}
          />
          {HALLOWEEN && (
            // Perched on the tallest of the logo's towers.
            <WitchHat
              style={{
                position: "absolute",
                top: -34,
                left: "calc(60% - 34px)",
                width: 68,
                transform: "rotate(12deg)",
                pointerEvents: "none",
              }}
            />
          )}
          {CHRISTMAS && (
            // Perched on the tallest of the logo's towers.
            <Sprite
              atlas={ICONS}
              name="santaHat"
              scale={0.62}
              style={{
                position: "absolute",
                top: -36,
                left: "calc(60% - 34px)",
                transform: "rotate(14deg)",
                pointerEvents: "none",
              }}
            />
          )}
        </Box>

        <Typography
          variant="body1"
          sx={
            HALLOWEEN
              ? {
                  fontFamily: SPOOKY_FONT,
                  // One weight only — a faked bold smears the drips.
                  fontWeight: 400,
                  fontSize: { xs: "1.5rem", md: "1.9rem" },
                  lineHeight: 1.2,
                  color: NIGHT.pumpkin,
                  letterSpacing: "0.02em",
                  textShadow: "0 0 22px rgba(255,122,26,0.5)",
                }
              : CHRISTMAS
              ? {
                  fontFamily: PIXEL_FONT,
                  fontWeight: 400,
                  fontSize: { xs: "1.05rem", md: "1.4rem" },
                  lineHeight: 1.6,
                  color: FROST.gold,
                  letterSpacing: "0.02em",
                  textShadow: "0 3px 0 " + FROST.redDeep + ", 0 0 22px rgba(255,207,90,0.4)",
                }
              : { color: "rgba(255,255,255,0.72)", letterSpacing: "0.02em" }
          }
        >
          Оренда житла у Львові
        </Typography>

        <Button
          component={RouterLink}
          to="/admin"
          size="large"
          startIcon={<LoginOutlined />}
          sx={{
            mt: 1,
            px: 3.5,
            py: 1.4,
            borderRadius: "999px",
            backgroundColor: tokens.surface,
            color: tokens.blue800,
            fontWeight: 700,
            boxShadow: "0 10px 30px rgba(4,20,43,0.45)",
            "&:hover": { backgroundColor: tokens.blue50 },
            ...(HALLOWEEN
              ? {
                  background: "linear-gradient(180deg, #FF9A3D 0%, " + NIGHT.pumpkinDeep + " 100%)",
                  color: "#fff",
                  boxShadow: "0 10px 30px rgba(228,87,11,0.45)",
                  "&:hover": {
                    background: "linear-gradient(180deg, #FFA852 0%, #D14E08 100%)",
                  },
                }
              : {}),
            ...(CHRISTMAS
              ? {
                  background: "linear-gradient(180deg, " + FROST.red + " 0%, " + FROST.redDeep + " 100%)",
                  color: "#fff",
                  boxShadow: "0 10px 30px rgba(163,24,44,0.5)",
                  "&:hover": {
                    background: "linear-gradient(180deg, #F04A57 0%, #8E1426 100%)",
                  },
                }
              : {}),
          }}
        >
          Вхід для рієлторів
        </Button>

        {/*
          The maintenance page replaces the whole layout, header included, so
          without these two the crossword and the contacts page are only
          reachable by typing the URL. Kept quiet on purpose: the realtor login
          stays the one prominent action.
        */}
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={{ xs: 0.5, sm: 1.5 }}
          alignItems="center"
          sx={{ pt: 1 }}
        >
          {SECONDARY.map(function (link) {
            return (
              <Button
                key={link.to}
                component={RouterLink}
                to={link.to}
                size="small"
                startIcon={link.icon}
                sx={{
                  px: 1.8,
                  borderRadius: "999px",
                  fontWeight: 500,
                  color: "rgba(255,255,255,0.72)",
                  "&:hover": {
                    color: tokens.surface,
                    backgroundColor: "rgba(255,255,255,0.10)",
                  },
                }}
              >
                {link.label}
              </Button>
            );
          })}
        </Stack>
      </Stack>
    </Box>
  );
}
